import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Template, Product as MockProduct, isUserLoggedIn } from '../data/mockData';
import { addToCart } from '../utils/cartManager';
import ProductDesignRenderer from './ProductDesignRenderer';
import ProductLayersEditor from './ProductLayersEditor';
import { mergeDesignToImage, getAllGlobalMergedImages, loadImageWithCORS } from '../utils/imageUtils';
import { exportImages } from '../utils/render3d'; 
import { request } from '../utils/request';
import { ApiProduct, ApiTemplate } from '../utils/resourceUtils';
import { getGlobalResources } from '../utils/globalState';
import { applyResourcesToProducts } from '../utils/designUtils';
import ProductCard from './ProductCard';
import { OSS_BUCKET } from '../config';

import {
  StarIcon,
  HeartIcon,
  ShareIcon,
  CheckIcon,
  ChevronDownIcon,
  SparklesIcon,
  RectangleStackIcon,
  GiftIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid, HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { baseImageToOss, generateRandomCode } from '../utils/common';

// 定义 MergedImageData 接口
interface MergedImageData {
  image: string;
  code: string;
  timestamp: number;
}

// Interface for product API response
interface ProductResponse {
  success: boolean;
  code: number;
  message: string;
  result: ApiProduct;
}

const ProductDetail: React.FC = () => {
  const navigate = useNavigate();
  const handleCustomizeClick = (product: ApiProduct) => {
    const cacheKey = generateRandomCode();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const cacheData = {
      key: cacheKey,
      product: product,
      timestamp: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      version: '1.0'
    };

    localStorage.setItem(`design_${cacheKey}`, JSON.stringify(cacheData));

    // 跳转到设计页面
    navigate(`/product/${product.id}/${cacheKey}`);
  };
  const { id, cacheKey } = useParams<{ id: string; cacheKey?: string }>();
  const [product, setProduct] = useState<MockProduct | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
 
  const [quantity, setQuantity] = useState(1);
  // variant selections derived from optionCombinations
  const [optionCombinations, setOptionCombinations] = useState<any[]>([]);
  const [colorOptions, setColorOptions] = useState<Array<{ name: string; src?: string }>>([]);
  const [sizeOptions, setSizeOptions] = useState<Array<{ name: string }>>([]);
  const [selectedColorName, setSelectedColorName] = useState<string>('');
  const [selectedSizeName, setSelectedSizeName] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [showQuantitySelect, setShowQuantitySelect] = useState(false);
  const [processedImageData, setProcessedImageData] = useState<{ [key: number]: any[] }>({});
  const [selectedOptionValueId, setSelectedOptionValueId] = useState('');
  const [sliderImages, setSliderImages] = useState<{ [key: number]: string[] }>({});
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [loadedProduct, setLoadedProduct] = useState<MockProduct | null>(null);
  const [isLoadingCache, setIsLoadingCache] = useState(false);
  const rightSidebarRef = useRef<HTMLDivElement>(null);
  const [textLayers, setTextLayers] = useState<Array<{ id: string, text: string, options: any }>>([]);
  const [frameLayers, setFrameLayers] = useState<Array<{ id: string, url: string, ai?: string, options: any }>>([]);
  const [isLayersExtracted, setIsLayersExtracted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); 


  // 喜欢相关状态
  const [isLiked, setIsLiked] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // 推荐产品相关状态
  const [recommendedProducts, setRecommendedProducts] = useState<MockProduct[]>([]);
  const [isLoadingRecommended, setIsLoadingRecommended] = useState(false);

  // 样式相关状态
  const [styles, setStyles] = useState<any[]>([]);
  const [isLoadingStyles, setIsLoadingStyles] = useState(false);
  // 产品描述展开/收起
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  // 特殊需求状态
  const [specialRequirements, setSpecialRequirements] = useState('');

  // Load product from API
  // 检查产品是否已被喜欢
  const checkLikeStatus = async (productId: string) => {
    try {
      const response: any = await request({
        method: 'GET',
        url: `likes/check/${productId}`
      });

      if (response.success && response.result) {
        setIsLiked(response.result.isLiked);
      }
    } catch (error) {
      console.error('检查喜欢状态失败:', error);
    }
  };

  // 切换喜欢状态
  const toggleLike = async () => {
    if (!loadedProduct || isLikeLoading) return;

    setIsLikeLoading(true);
    try {
      if (isLiked) {
        // 取消喜欢
        const response: any = await request({
          method: 'DELETE',
          url: `likes/${cacheKey || loadedProduct.id}`
        });

        if (response.success) {
          setIsLiked(false);
        }
      } else {
        // 添加喜欢
        const response: any = await request({
          method: 'POST',
          url: 'likes',
          data: {
            productId: loadedProduct.id,
            code: cacheKey || loadedProduct.id, // 使用产品ID作为code
            designer: 'User', // 可以根据需要设置设计师名称
            isPublic: true
          }
        });

        if (response.success) {
          setIsLiked(true);
        }
      }
    } catch (error) {
      console.error('切换喜欢状态失败:', error);
    } finally {
      setIsLikeLoading(false);
    }
  };

  // 复制当前地址到剪切板
  const handleShare = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);

      // 显示复制成功提示
      setShowCopySuccess(true);
      setTimeout(() => {
        setShowCopySuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // 降级方案：使用传统的复制方法
      try {
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        setShowCopySuccess(true);
        setTimeout(() => {
          setShowCopySuccess(false);
        }, 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      }
    }
  };

  const loadProduct = async () => {
    if (!id && !cacheKey) return;
    setIsLoading(true);
    setError(null);
    console.log('Loading product with cacheKey:', cacheKey || id);
    try {
      const response: ProductResponse = await request({
        url: `products/${cacheKey || id}`,
        method: 'GET'
      });
      console.log('API Response:', response);
      if (response.success) {
        // Transform API product to match frontend Product interface
        const transformedProduct: MockProduct = {
          id: response.result.id.toString(),
          name: response.result.name,
          description: response.result.description || '',
          category: response.result.category,
          price: response.result.basePrice || response.result.price || 0,
          image: response.result.image || '',
          rating: response.result.rating || 0,
          reviews: response.result.reviews || 0,
          sliderImages: response.result.sliderImages || '',
          customizable: true, // Default to true for API products
          templates: (response.result.templates || []).map((template: ApiTemplate) => ({
            id: template.id.toString(),
            name: template.name,
            preview: '', // API doesn't provide preview, use empty string
            design: template.design
          })),
          colors: [], // Default empty colors array
          sizes: [], // Default empty sizes array
          previews: response.result.previews || []
        };
        setProduct(transformedProduct);
        setLoadedProduct(transformedProduct);
        // 检查喜欢状态
        checkLikeStatus(transformedProduct.id);
        setSelectedTemplate(transformedProduct.templates[0]);

        // Derive 2D options from optionCombinations
        const combos = Array.isArray((response as any).result?.optionCombinations) ? (response as any).result.optionCombinations : [];
        setOptionCombinations(combos);
        if (combos.length > 0) {
          const colorMap = new Map<string, string | undefined>();
          const sizeSet = new Set<string>();
          combos.forEach((c: any) => {
            (c.options || []).forEach((opt: any) => {
              if (opt.groupName === 'Color' && opt.optionName) {
                if (!colorMap.has(opt.optionName)) colorMap.set(opt.optionName, opt.src);
              }
              if (opt.groupName === 'Size' && opt.optionName) {
                sizeSet.add(opt.optionName);
              }
            });
          });
          const colors = Array.from(colorMap.entries()).map(([name, src]) => ({ name, src }));
          const sizes = Array.from(sizeSet.values()).map((name) => ({ name }));
          setColorOptions(colors);
          setSizeOptions(sizes);
          if (colors.length > 0) setSelectedColorName(colors[0].name);
          if (sizes.length > 0) setSelectedSizeName(sizes[0].name);
        }
      } else {
        setError(response.message || 'Failed to load product');
      }
    } catch (error: any) {
      console.error('Load product error:', error);
      setError('Failed to load product');
    } finally {
      setIsLoading(false);
    }
  };

  // 加载推荐产品
  const loadRecommendedProducts = async () => {
    try {
      setIsLoadingRecommended(true);
      const response: any = await request({
        method: 'GET',
        url: `/products?type=guess&limit=4`
      });

      if (response.success && response.result) {
        // 转换API产品数据为MockProduct格式
        const transformedProducts = response.result.products.map((apiProduct: any) => ({
          id: apiProduct.id.toString(),
          name: apiProduct.name,
          category: apiProduct.category,
          price: apiProduct.price,
          image: apiProduct.image,
          description: apiProduct.description,
          customizable: apiProduct.customizable || true,
          colors: apiProduct.colors || [],
          sizes: apiProduct.sizes || [],
          rating: apiProduct.rating || 0,
          reviews: apiProduct.reviews || 0,
          previews: apiProduct.previews || [],
          sliderImages: apiProduct.sliderImages || '',
          templates: apiProduct.templates?.map((apiTemplate: any) => ({
            id: apiTemplate.id.toString(),
            name: apiTemplate.name,
            preview: apiTemplate.preview,
            design: apiTemplate.design
          })) || []
        }));
        // 应用全局资源到推荐产品
        let products = applyResourcesToProducts(transformedProducts, getGlobalResources());
        setRecommendedProducts(products);
      }
    } catch (error) {
      console.error('加载推荐产品失败:', error);
    } finally {
      setIsLoadingRecommended(false);
    }
  };

  // 加载样式列表
  const loadStyles = async () => {
    try {
      setIsLoadingStyles(true);
      const response: any = await request({
        method: 'GET',
        url: `products?type=design&limit=6&productId=${loadedProduct?.id}&excludeSelf=true`
      });

      if (response.success && response.result) {
        let products = applyResourcesToProducts(response.result.products, getGlobalResources());
        setStyles(products);
      }
    } catch (error) {
      console.error('加载样式失败:', error);
    } finally {
      setIsLoadingStyles(false);
    }
  };

  // Load product when component mounts or id changes
  useEffect(() => {
    // First try to load from cache if cacheKey exists
    if (cacheKey) {
      const visitKey = 'FIRST_VISIT_CODE';
      // localStorage.setItem(visitKey,"");
      const lastVisit = localStorage.getItem(visitKey);
      const now = new Date().getTime();
      const ONE_DAY = 24 * 60 * 60 * 1000;

      let cacheLoaded = false;
      let shouldLoadCache = true;

      if (lastVisit) {
        try {
          const visitData = JSON.parse(lastVisit);
          // If visited within last 24h, skip
          if (now - visitData.timestamp < ONE_DAY) {
            shouldLoadCache = false;
            console.log('Cache load skipped: Visited within last 24h');
          }
        } catch (e) {
          // If parsing fails, treat as new visit
          console.error('Error parsing visit data', e);
        }
      }

      if (shouldLoadCache) {
        localStorage.setItem(visitKey, JSON.stringify({
          code: cacheKey,
          timestamp: now
        }));
        console.log('cacheLoaded', cacheLoaded);
      }
      

      cacheLoaded = loadCachedDesign(cacheKey);
      console.log('cacheLoaded', cacheLoaded);
      if (!cacheLoaded) {
        loadProduct();
      }
    } else {
      // No cacheKey, load from API directly
      loadProduct();
    }
    loadRecommendedProducts();
  }, [id, cacheKey]);

  // Load styles when product is loaded
  useEffect(() => {
    if (loadedProduct) {
      loadStyles();
    }
  }, [loadedProduct]);

  // 页面加载时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
    // 当产品ID变化时，重置初始化标志
    setIsInitialized(false);
    setIsLayersExtracted(false);
  }, [id]);

  // 添加滚动事件监听器，优先滚动右侧边栏
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (rightSidebarRef.current) {
        // 检查鼠标是否在右侧边栏区域内
        const rect = rightSidebarRef.current.getBoundingClientRect();
        const isMouseOverSidebar = e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom;

        if (isMouseOverSidebar) {
          e.preventDefault();
          rightSidebarRef.current.scrollTop += e.deltaY;
        }
      }
    };

    // 监听整个页面的滚动事件
    document.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // 加载缓存设计的函数
  const loadCachedDesign = (cacheKey: string): boolean => {
    try {
      setIsLoadingCache(true);
      const cachedData = localStorage.getItem(`design_${cacheKey}`);
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);

        // 检查是否过期
        if (parsedData.expiresAt) {
          const expiresAt = new Date(parsedData.expiresAt);
          const now = new Date();

          if (now > expiresAt) {
            // 缓存已过期，删除它
            localStorage.removeItem(`design_${cacheKey}`);
            console.warn('Design has expired and has been removed from cache');
            return false;
          }
        }
        console.log('parsedData', parsedData);
        if (parsedData.product) {
          setLoadedProduct(parsedData.product);
          setProduct(parsedData.product); // Also set the main product state 
          if (parsedData.product.templates && parsedData.product.templates.length > 0) {
            setSelectedTemplate(parsedData.product.templates[0]);
          }

          // derive 2D options from cached optionCombinations
          const combos = Array.isArray(parsedData.product.optionCombinations) ? parsedData.product.optionCombinations : [];
          setOptionCombinations(combos);
          if (combos.length > 0) {
            const colorMap = new Map<string, string | undefined>();
            const sizeSet = new Set<string>();
            combos.forEach((c: any) => {
              (c.options || []).forEach((opt: any) => {
                if (opt.groupName === 'Color' && opt.optionName) {
                  if (!colorMap.has(opt.optionName)) colorMap.set(opt.optionName, opt.src);
                }
                if (opt.groupName === 'Size' && opt.optionName) {
                  sizeSet.add(opt.optionName);
                }
              });
            });
            const colors = Array.from(colorMap.entries()).map(([name, src]) => ({ name, src }));
            const sizes = Array.from(sizeSet.values()).map((name) => ({ name }));
            setColorOptions(colors);
            setSizeOptions(sizes);
            if (colors.length > 0) setSelectedColorName(prev => prev || colors[0].name);
            if (sizes.length > 0) setSelectedSizeName(prev => prev || sizes[0].name);
          }

          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error loading from cache:', error);
      return false;
    } finally {
      setIsLoadingCache(false);
    }
  };

  // 初始化selectedTemplate
  useEffect(() => {
    // 如果已经初始化过，不再重复执行
    if (isInitialized) {
      return;
    }

    // 当产品数据加载完成后，设置默认模板
    if (loadedProduct && loadedProduct.templates.length > 0) {
      setSelectedTemplate(loadedProduct.templates[0]);
    }

    setIsInitialized(true); // 标记为已初始化
  }, [loadedProduct, isInitialized]);

  // 当selectedTemplate或loadedProduct更新时，提取图层信息
  useEffect(() => {
    // 如果已经提取过图层信息，不再重复执行
    if (isLayersExtracted) {
      // return;
    }

    const currentTemplate = selectedTemplate || (loadedProduct?.templates?.[0] || null); 
    if (currentTemplate && currentTemplate.design) {
      const design = currentTemplate.design;

      if (design.faces && design.faces[0] && design.faces[0].layers) {
        const textLayersData: Array<{ id: string, text: string, options: any }> = [];
        const frameLayersData: Array<{ id: string, url: string, ai: string, options: any }> = [];

        design.faces[0].layers.forEach((layer: any, index: number) => {
          // 跳过被锁定的图层
          if (layer.options?.lock === true) {
            return;
          }
          if (layer.type === 'IText') {
            textLayersData.push({
              id: `text-${index}`,
              text: layer.text || '',
              options: layer.options || {}
            });
          } else if (layer.type !== 'IText') {
            if (layer.fill.src) {
              frameLayersData.push({
                id: `${layer.type}-${index}`,
                url: layer.fill.src || '',
                ai: layer.ai || '',
                options: layer.options || {}
              });
            } else if (layer.src) {
              frameLayersData.push({
                id: `${layer.type}-${index}`,
                url: layer.src || '',
                ai: layer.ai || '',
                options: layer.options || {}
              });
            }
          }
        });
        setTextLayers(textLayersData);
        setFrameLayers(frameLayersData);
      }
    }
  }, [selectedTemplate, loadedProduct]);


  // 处理所有模板图片的函数
  const processAllTemplateImages = async () => {
    try {
      if (loadedProduct && loadedProduct.templates) {
        const firstTemplate = loadedProduct.templates[0];
        if (firstTemplate.design) {
          await mergeDesignToImage('', firstTemplate.design, 800);
        }

        for (let i = 0; i < loadedProduct.templates.length; i++) {
          const template = loadedProduct.templates[i];

          if (!template.design) {
            continue;
          }

          // 将数组转换为对象格式，以 code 为键
          const mergedImagesArray = getAllGlobalMergedImages();


          const exportResult = await exportImages(
            { "screenshot": mergedImagesArray, "urlPath": "http://localhost:3000/" },
            template.design
          )

          // exportImages 返回的是 tempImageAll 对象，需要提取图片数据
          if (exportResult.length) {
            setProcessedImageData(prev => {
              const newData = {
                ...prev,
                [i]: exportResult
              };
              return newData;
            });
          } else {
            console.log(`模板 ${i} 没有有效的导出结果`);
          }
        }
      } else {
        console.log("没有找到 loadedProduct 或 templates");
      }
    } catch (error) {
      console.error('处理所有模板图片失败:', error);
    }
  };

  // 页面加载时处理所有模板图片
  useEffect(() => {
    console.log("refresh all templates images....")
    setTimeout(() => {
      processAllTemplateImages();
    }, 400);
  }, [loadedProduct]);
 
   

  // 监听 processedImageData 的变化
  useEffect(() => {
    let optionsValueId = selectedOptionValueId
    processedImageData[0]?.forEach((item: any) => {
      if (item?.optionName === selectedColorName) {
        optionsValueId = item?.optionsValueId || '';
        setSelectedOptionValueId(optionsValueId);
      }
    })
    // 将 options_value_id 为 175 的图片提取到 sliderImages
    const nextSlider: { [key: number]: string[] } = {};
    Object.keys(processedImageData).forEach((keyStr) => {
      const key = Number(keyStr);
      const data = processedImageData[key];
      if (!Array.isArray(data)) return;
      if (!optionsValueId) setSelectedOptionValueId("1");
      if (typeof data[0] === 'string') {
        nextSlider[key] = data as string[];
      } else {
        const entry175 = (data as any[]).find((entry: any) => String(entry?.optionsValueId) === optionsValueId);
        if (entry175 && Array.isArray(entry175.image)) {
          nextSlider[key] = entry175.image as string[];
        } else {
          const allImages = (data as any[]).flatMap((entry: any) => entry?.image || []);
          nextSlider[key] = allImages;
        }
      }
    });
    setSliderImages(nextSlider);
  }, [processedImageData, selectedColorName]);

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: '#2e3a47' }}></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading product...</h2>
          <p className="text-gray-600">Please wait while we load the product details.</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-x-4">
            <button
              onClick={loadProduct}
              className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#2e3a47' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
            >
              Try Again
            </button>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Back to Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Product not found
  if (!product || !loadedProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {isLoadingCache ? 'Loading design...' : 'Product not found'}
          </h2>
          <Link
            to="/products"
            className="transition-colors"
            style={{ color: '#2e3a47' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#3a4a57'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#2e3a47'}
          >
            Back to products
          </Link>
        </div>
      </div>
    );
  }

  const selectedCombination = optionCombinations.find((c: any) => {
    const names = (c.options || []).reduce((acc: any, o: any) => {
      if (o.groupName === 'Color') acc.color = o.optionName;
      if (o.groupName === 'Size') acc.size = o.optionName;
      return acc;
    }, { color: '', size: '' });
    return (!selectedColorName || names.color === selectedColorName) && (!selectedSizeName || names.size === selectedSizeName);
  });

  const handleTextChange = (id: string, newContent: string) => {
    setTextLayers(prev => prev.map(layer =>
      layer.id === id ? { ...layer, text: newContent } : layer
    ));

    // 更新模板中的图层数据
    if (selectedTemplate && selectedTemplate.design) {
      const updatedDesign = { ...selectedTemplate.design };
      const layerIndex = parseInt(id.split('-')[1]);

      if (updatedDesign.faces && updatedDesign.faces[0] && updatedDesign.faces[0].layers && updatedDesign.faces[0].layers[layerIndex]) {
        const originalLayer = updatedDesign.faces[0].layers[layerIndex];
        const originalText = originalLayer.text || '';
        const originalFontSize = originalLayer?.fontSize || 50;
        const fontFamily = originalLayer?.fontFamily || 'Arial';
        updatedDesign.faces[0].layers[layerIndex] = {
          ...originalLayer,
          text: newContent,
          fontSize: originalFontSize
        };
        const updatedTemplate = {
          ...selectedTemplate,
          design: updatedDesign
        };
        setSelectedTemplate(updatedTemplate);
        // 重新处理所有模板图片
        setTimeout(() => {
          processAllTemplateImages();
        }, 400);
      }
    }
  };
  const handleFrameAIChange = (id: string, ai: string | undefined, url?: string) => {
    console.log('handleFrameAIChange', id, ai, url);
    setFrameLayers(prev => prev.map(layer =>
      layer.id === id ? { ...layer, ai: ai } : layer
    ));
    if (selectedTemplate && selectedTemplate.design) {
      const updatedDesign = { ...selectedTemplate.design };
      const layerIndex = parseInt(id.split('-')[1]);

      if (updatedDesign.faces && updatedDesign.faces[0] && updatedDesign.faces[0].layers && updatedDesign.faces[0].layers[layerIndex]) {
        if (url) {
          updatedDesign.faces[0].layers[layerIndex] = {
            ...updatedDesign.faces[0].layers[layerIndex],
            ai: ai,
            src: url
          };
        } else {
          updatedDesign.faces[0].layers[layerIndex] = {
            ...updatedDesign.faces[0].layers[layerIndex],
            ai: ai
          };
        }
        updatedDesign.faces[0].layers[layerIndex] = {
          ...updatedDesign.faces[0].layers[layerIndex],
          ai: ai
        };

        const updatedTemplate = {
          ...selectedTemplate,
          design: updatedDesign
        };

        setSelectedTemplate(updatedTemplate);
        // 重新处理所有模板图片
        setTimeout(() => {
          processAllTemplateImages();
        }, 400);
      }
    }
  };
  const handleFrameImageChange = async (id: string, newImage: string) => {
    setFrameLayers(prev => prev.map(layer =>
      layer.id === id ? { ...layer, url: newImage } : layer
    ));
    // 更新模板中的图层数据
    if (selectedTemplate && selectedTemplate.design) {
      const updatedDesign = { ...selectedTemplate.design };
      const layerIndex = parseInt(id.split('-')[1]);
      if (updatedDesign.faces && updatedDesign.faces[0] && updatedDesign.faces[0].layers && updatedDesign.faces[0].layers[layerIndex]) {
        // 修改图片
        if (updatedDesign.faces[0].layers[layerIndex].type === 'Image') {

          const img = await loadImageWithCORS(newImage + "?x-oss-process=style/large");

          let { options, width, height } = updatedDesign.faces[0].layers[layerIndex]
          const arImage = img.width / img.height;
          const arCanvas = (options?.width ? width / options.width : width) / (options?.height ? height / options.height : height);

          let normLeft = 0;
          let normTop = 0;
          let iwidth = 1; let iheight = 1;
          if (arImage >= arCanvas) {
            iwidth = arImage / arCanvas; // 宽可能 > 1  
            normLeft = -(iwidth - 1) / 2; // 居中裁剪
            normTop = 0;
          } else {
            iheight = arCanvas / arImage; // 高可能 > 1 
            normLeft = 0;
            normTop = -(iheight - 1) / 2; // 居中裁剪
          }
          updatedDesign.faces[0].layers[layerIndex] = {
            ...updatedDesign.faces[0].layers[layerIndex],
            src: newImage,
            left: normLeft,
            top: normTop,
            width: img.width,
            height: img.height,
            options: {
              left: 0.5 + normLeft / 2,
              top: 0.5 + normTop / 2,
              width: iwidth,
              height: iheight,
            }
          };
          // 修改图层填充
        } else {
          updatedDesign.faces[0].layers[layerIndex].fill.src = newImage
        }

        console.log(updatedDesign.faces[0].layers[layerIndex])

        const updatedTemplate = {
          ...selectedTemplate,
          design: updatedDesign
        };

        setSelectedTemplate(updatedTemplate);
        // 重新处理所有模板图片
        processAllTemplateImages();
      }
    }
  };

  // 全局桥接：允许其他组件（如 ResourcesPopup）触发图层图片更新
  const GLOBAL_FRAME_APPLY_KEY = '__GiftMkr_onFrameImageChange__';
  (globalThis as any)[GLOBAL_FRAME_APPLY_KEY] = (which: string, url: string, ai: string = '') => {
    try {
      const firstId = frameLayers.find(layer => layer.id.startsWith('Image-') || layer.id.startsWith('Circle-') || layer.id.startsWith('Rect-'))?.id;
      const targetId = (which === 'first' || which === '1') ? firstId : which;
      if (targetId) {
        // handleFrameImageChange(targetId, url);
        handleFrameAIChange(targetId, ai, url);
      }
    } catch (err) {
      console.warn('Global onFrameImageChange bridge failed:', err);
    }
  };


  // 更新产品模板图层的函数
  const updateProductTemplateLayers = () => {
    if (!loadedProduct || !selectedTemplate) return loadedProduct;

    const updatedProduct = {
      ...loadedProduct,
      templates: loadedProduct.templates.map(template => {
        if (template.id === selectedTemplate?.id && template.design) {
          // 更新设计中的图层
          const updatedLayers = template.design.faces[0].layers.map((layer: any, index: number) => {
            // 更新文本图层
            const textLayer = textLayers.find(tl => tl.id === `text-${index}`);
            if (textLayer && layer.type === 'text') {
              return {
                ...layer,
                text: textLayer.text
              };
            }

            // 更新图片/相框图层
            const frameLayer = frameLayers.find(fl => fl.id === `${layer.type}-${index}`);
            if (frameLayer && (layer.type === 'frame' || layer.type === 'image')) {
              return {
                ...layer,
                url: frameLayer.url,
                ai: frameLayer.ai
              };
            }

            return layer;
          });
          return {
            ...template,
            design: {
              ...template.design,
              faces: [{
                ...template.design.faces[0],
                layers: updatedLayers
              }]
            }
          };
        }
        return template;
      })
    };

    return updatedProduct;
  };

  const handleAddToCart = async () => {
    // 检查是否已登录，未登录则打开登录窗口并终止后续加购流程
    if (!isUserLoggedIn()) {
      window.dispatchEvent(new Event('openLoginModal'));
      return;
    }
    try { 
      // 获取当前选中的template的design（使用loadedProduct数据）
      const currentTemplate = selectedTemplate || loadedProduct.templates[0];
      let mergedImageUrl = loadedProduct.image; // 默认使用产品图片



      // 获取processedImageData中第一组的data:开头的数据
      let base64ImageData = null;
      const firstGroupIndex = Object.keys(processedImageData)[0];
      if (firstGroupIndex && processedImageData[parseInt(firstGroupIndex)]) {
        const firstGroupImages = processedImageData[parseInt(firstGroupIndex)];
        let ary = firstGroupImages.find(obj => obj.optionsValueId === selectedOptionValueId);
        if (!ary) ary = firstGroupImages[0];
        base64ImageData = ary.image.find((img: string) => img && img.startsWith('data:'));
      }

      if (base64ImageData && cacheKey) {
        baseImageToOss(cacheKey + ".png", base64ImageData, OSS_BUCKET);
      }

      // 使用addToCart函数添加到购物车，传入合并后的图片
      await addToCart(
        parseInt(loadedProduct.id),
        cacheKey || loadedProduct.id,
        quantity,
        selectedSizeName,
        selectedColorName,
        loadedProduct.templates ? { "templates": loadedProduct.templates } : undefined,
        specialRequirements
      );

      // 显示添加成功状态
      setIsAddedToCart(true);

      // 3秒后重置状态
      setTimeout(() => {
        setIsAddedToCart(false);
      }, 3000);
    } catch (error) {
      console.error('添加到购物车失败:', error);
    }
  };
  if (getGlobalResources().length == 0)
  loadedProduct.previews?.forEach((review: any) => {
    if (!review.url) review.url = loadedProduct.image;
  })
  // 合并templates和样本图片
  const productImages = [
    // 首先添加templates
    ...loadedProduct.templates.map((template, index) => ({
      id: `template-${index}`,
      preview: template.preview,
      alt: template.name,
      isTemplate: template.id=="0"?false:true,
      template: template
    })),
    // 然后添加 sliderImages（逗号分隔的图片地址）
    ...(loadedProduct && loadedProduct.sliderImages
      ? loadedProduct.sliderImages
        .split(',')
        .map((url: string, idx: number) => ({
          id: `slider-${idx}`,
          preview: url.trim(),
          alt: `Slider ${idx + 1}`,
          isTemplate: false,
          template: {
            id: `slider-${idx}`,
            name: `Slider ${idx + 1}`,
            preview: url.trim()
          }
        }))
      : [])
  ]; 
  return (
    <div className="min-h-screen bg-white py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile Layout */}
        <div className="block lg:hidden">
          {/* Mobile Product Images */}
          <div className="mb-6">
            {/* Main Image */}
            <div className="relative mb-4">
              <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center relative">

                {/* Share and Favorite Icons */}
                <div className="absolute top-4 right-4 flex space-x-2 z-[300]">
                  <button
                    onClick={handleShare}
                    className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors relative"
                    title="copy link"
                  >
                    <ShareIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    {showCopySuccess && (
                      <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        Copied
                      </div>
                    )}
                  </button>
                  <button
                    onClick={toggleLike}
                    disabled={isLikeLoading}
                    className={`w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors ${isLikeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isLiked ? (
                      <HeartIconSolid className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                    ) : (
                      <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    )}
                  </button>
                </div>
                {selectedTemplate && selectedTemplate.design ? (
                  <ProductDesignRenderer
                    design={selectedTemplate.design}
                    previews={product.previews}
                    // selectedColorName={selectedColorName}
                    optionValueId={selectedOptionValueId}
                    processedImage={sliderImages[selectedImage]}
                    containerWidth={400}
                    containerHeight={400}
                    index="mobile"
                    className="w-4/5 h-4/5"
                  />
                ) : (
                  selectedTemplate && selectedTemplate.preview ? (
                    <img
                      src={selectedTemplate.preview}
                      alt={selectedTemplate.name || 'Template Preview'}
                      className="w-4/5 h-4/5 object-contain bg-white rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="w-4/5 h-4/5 bg-white rounded-lg shadow-lg flex items-center justify-center">
                      <PhotoIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300" />
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Mobile Thumbnails */}
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
              {productImages.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => {
                    setSelectedImage(index);
                    // if (image.isTemplate && 'template' in image) {
                    setSelectedTemplate(image.template);
                    // }
                  }}
                  className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg border-2 overflow-hidden transition-all ${selectedImage === index
                    ? ''
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  style={selectedImage === index ? { borderColor: '#2e3a47' } : {}}
                >
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                    {image.isTemplate ? (
                      sliderImages[index] && sliderImages[index].length > 0 ? (
                        <div className="w-full h-full relative">
                          {sliderImages[index].map((imgSrc, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={imgSrc}
                              alt={`Processed Template Layer ${imgIndex + 1}`}
                              className="absolute inset-0 w-full h-full object-cover"
                              style={{ zIndex: imgIndex }}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs sm:text-sm"></span>
                      )
                    ) : (
                      <img
                        src={image.preview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Product Information */}
          <div className="space-y-4">
            {/* Pricing */}
            <div>
              <div className="flex items-baseline space-x-2 mb-2">
                <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                  ${selectedCombination?.price || loadedProduct.price}
                </span>
                <span className="text-base sm:text-lg text-gray-500 line-through">
                  ${(((Number(selectedCombination?.price) || loadedProduct.price)) * 1.15).toFixed(2)}
                </span>
              </div>
              {/* <p className="text-xs sm:text-sm text-green-600 font-medium">
                Save 15% with code AUGUSTSAVE15
              </p> */}
            </div>

            {/* Product Title */}
            <div>
              <h1 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-2">
                {(loadedProduct.name || '').length > 60
                  ? `${(loadedProduct.name || '').slice(0, 60)}...`
                  : (loadedProduct.name || '')}
                {cacheKey && (
                  <span className="ml-2 text-sm px-2 py-1 rounded" style={{ color: '#2e3a47', backgroundColor: '#f0f2f5' }}>
                    Custom Design
                  </span>
                )}
              </h1>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIconSolid
                        key={star}
                        className={`h-3 w-3 sm:h-4 sm:w-4 ${star <= (loadedProduct.rating || 0) ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs sm:text-sm text-gray-600">
                    ({loadedProduct.reviews ? loadedProduct.reviews.toLocaleString() : '0'})

                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">by Sweet Rain Paperie</p>
              </div>
            </div>

            {/* Product Description */}
            {loadedProduct.description && (
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                <p className="text-xs sm:text-sm text-gray-700">
                  {isDescriptionExpanded
                    ? loadedProduct.description
                    : ((loadedProduct.description || '').length > 140
                      ? (loadedProduct.description || '').slice(0, 140) + '…'
                      : (loadedProduct.description || ''))}
                </p>
                {(loadedProduct.description || '').length > 60 && (
                  <button
                    type="button"
                    className="mt-2 text-xs sm:text-sm font-medium"
                    style={{ color: '#2e3a47' }}
                    onClick={() => setIsDescriptionExpanded(prev => !prev)}
                  >
                    {isDescriptionExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}

            {/* Mobile Special Requirements */}
            {cacheKey?.startsWith('AI') && (
              <div className="p-3 sm:p-4 rounded-lg" style={{ backgroundColor: '#f0f2f5' }}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements</label>
                <textarea
                  value={specialRequirements}
                  onChange={(e) => setSpecialRequirements(e.target.value)}
                  placeholder="Please enter more customization requirements"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  rows={3}
                />
              </div>
            )}

            {/* Quantity and Add to Cart */}
            <div className="flex items-end space-x-3">
              <div className="w-32">
                <div className="flex items-center space-x-2">
                  <label className="text-xs sm:text-sm font-medium text-gray-700">Qty:</label>
                  <div className="relative flex-1">
                    {showQuantitySelect ? (
                      <select
                        value={quantity}
                        onChange={(e) => {
                          setQuantity(Number(e.target.value));
                          setShowQuantitySelect(false);
                        }}
                        onBlur={() => setShowQuantitySelect(false)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:border-transparent text-sm"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                        onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#2e3a47')}
                        autoFocus
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 50, 100].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={quantity}
                          onChange={(e) => setQuantity(Number(e.target.value) || 1)}
                          className="w-full border border-gray-300 rounded-lg px-3 pr-8 py-2 focus:ring-2 focus:border-transparent text-sm"
                          style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                          onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#2e3a47')}
                        />
                        <button
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer z-10"
                          onClick={() => setShowQuantitySelect(true)}
                        >
                          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={isAddedToCart}
                className={`flex-1 py-2 px-6 rounded-lg font-semibold transition-colors ${isAddedToCart
                  ? 'bg-green-500 text-white cursor-not-allowed'
                  : 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                  }`}
              >
                {isAddedToCart ? (
                  <div className="flex items-center justify-center">
                    <CheckIcon className="w-4 h-4 mr-1" />
                    Added
                  </div>
                ) : (
                  'Add to Cart'
                )}
              </button>

            </div>

            {/* Personalize Button */}
            <div className="space-y-2">

              {/* <Link
                to={`/customize/${loadedProduct.id}`}
                className="w-full text-white py-3 px-6 rounded-lg font-semibold transition-colors text-center block"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                Personalize this design
              </Link> */}
              {cacheKey && !cacheKey.startsWith('AI') && (
                <button
                  onClick={() => {
                    // 缓存当前的设计状态
                    const now = new Date();
                    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1天后过期

                    const updatedProduct = updateProductTemplateLayers()
                    const cacheData = {
                      key: cacheKey,
                      product: updatedProduct,
                      timestamp: now.toISOString(),
                      expiresAt: expiresAt.toISOString(),
                      version: '1.0'
                    };
                    console.log(cacheData)
                    localStorage.setItem(`design_${cacheKey}`, JSON.stringify(cacheData));

                    // 跳转到编辑页面
                    window.location.href = `/customize/${loadedProduct.id}?cacheKey=${cacheKey}`;
                  }}
                  className="w-full text-white py-2 px-6 rounded-lg font-medium transition-colors text-center block text-sm"
                  style={{ backgroundColor: '#68aeb0' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
                >
                  Personalize this design
                </button>
              )}
            </div>

            {/* Trending Indicator */}
            {/* <div className="flex items-center space-x-2 p-3 rounded-lg" style={{ backgroundColor: '#f0f2f5' }}>
              <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#2e3a47' }} />
              <div>
                <p className="text-xs" style={{ color: '#2e3a47' }}>{loadedProduct.reviews} people viewed this design</p>
              </div>
            </div> */}

            {/* Other Styles (match desktop) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm sm:text-base font-semibold text-gray-800">Other Styles</h4>
                <span className="text-xs sm:text-sm text-gray-600 font-medium">{styles.length} styles</span>
              </div>
              <div className="relative">
                {isLoadingStyles ? (
                  <div className="flex space-x-3 overflow-x-auto scrollbar-hide pb-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                      <div key={item} className="flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32 bg-gray-200 rounded-lg animate-pulse flex flex-col items-center justify-center border border-gray-200">
                        <div className="w-8 h-8 bg-gray-300 rounded mb-2"></div>
                        <div className="w-20 h-3 bg-gray-300 rounded"></div>
                      </div>
                    ))}
                  </div>
                ) : styles.length > 0 ? (
                  <div className="flex space-x-3 overflow-x-auto scrollbar-hide pb-2">
                    {styles.map((product, index) => (
                      product.templates?.[0]?.design && getGlobalResources().length > 0 ? (
                        // 使用ProductDesignRenderer组件（点击跳转到自定义设计）
                        <button
                          key={product.code+index}
                          onClick={() => handleCustomizeClick(product)}
                          className="flex-shrink-0 w-36 h-36 bg-gray-200 flex flex-col items-center justify-center border border-gray-200"
                        >
                          <ProductDesignRenderer
                            design={product.templates[0].design}
                            previews={product.previews}
                            containerWidth={400}
                            containerHeight={400}
                            index={"style" + index}
                            className="w-full h-full"
                          />
                        </button>
                      ) : (
                        // 如果没有design属性，使用默认图片显示（点击跳转到自定义设计）
                        <button
                          key={product.code}
                          onClick={() => handleCustomizeClick(product)}
                          className="flex-shrink-0 w-36 h-36 bg-gray-200 flex items-center justify-center border border-gray-200"
                        >
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </button>
                      )
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="flex justify-center mb-2">
                      <RectangleStackIcon className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-xs sm:text-sm">No styles available</p>
                  </div>
                )}
                <div className="absolute right-0 top-0 bottom-2 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid grid-cols-12 gap-8 items-start">

          {/* Left Sidebar - Thumbnails */}
          <div className="col-span-2">
            <div className="h-full flex flex-col px-2">
              <div className="flex-1 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">

                {productImages.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => {
                      setSelectedImage(index);
                      // if (image.isTemplate && 'template' in image) {
                      setSelectedTemplate(image.template);
                      // }
                    }}
                    className={`w-full aspect-square rounded-lg border-2 overflow-hidden transition-all flex-shrink-0 ${selectedImage === index
                      ? ''
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                    style={selectedImage === index ? { borderColor: '#2e3a47' } : {}}
                  > 
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                      {image.isTemplate ? (
                        sliderImages[index] && sliderImages[index].length > 0 ? (
                          <div className="w-full h-full relative">
                            {sliderImages[index].map((imgSrc, imgIndex) => (
                              <img
                                key={imgIndex}
                                src={imgSrc}
                                alt={`Processed Template Layer ${imgIndex + 1}`}
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ zIndex: imgIndex }}
                              />
                            ))}
                          </div>
                        ) : (
                          <PhotoIcon className="w-4 h-4 text-gray-400" />
                        )
                      ) : (
                        <img
                          src={image.preview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </button>
                ))}

              </div>
              <button className="w-1/2 py-1 text-gray-500 hover:text-gray-700 mt-2">
                <ChevronDownIcon className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>

          {/* Center - Main Product Display */}
          <div className="col-span-6">
            <div className="relative h-full">
              {/* Main Product Image */} 
              <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center relative">
              {selectedTemplate && selectedTemplate.design ? (
                  <ProductDesignRenderer
                    design={selectedTemplate.design}
                    previews={product.previews}
                    containerWidth={600}
                    containerHeight={600}
                    index="desktop"
                    optionValueId={selectedOptionValueId}
                    processedImage={sliderImages[selectedImage]}
                    className="w-4/5 h-4/5"
                  />
                  //  <PhotoIcon className="w-16 h-16 text-gray-300" />
                ) : (
                  selectedTemplate && selectedTemplate.preview ? (
                    <img
                      src={selectedTemplate.preview}
                      className="w-4/5 h-4/5 object-contain bg-white rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="w-4/5 h-4/5 bg-white rounded-lg shadow-lg flex items-center justify-center">
                      <PhotoIcon className="w-16 h-16 text-gray-300" />  
                    </div>
                  )
                )}

                {/* Share and Favorite Icons */}
                <div className="absolute top-4 right-4 flex space-x-2 z-[300]">
                  <button
                    onClick={handleShare}
                    className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors relative"
                    title="copy link"
                  >
                    <ShareIcon className="w-5 h-5 text-gray-600" />
                    {showCopySuccess && (
                      <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        Copied
                      </div>
                    )}
                  </button>
                  <button
                    onClick={toggleLike}
                    disabled={isLikeLoading}
                    className={`w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors ${isLikeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isLiked ? (
                      <HeartIconSolid className="w-5 h-5 text-red-500" />
                    ) : (
                      <HeartIcon className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="h-1/4">
              <div className="h-full">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base font-semibold text-gray-800">Other Styles</h4>
                  <span className="text-sm text-gray-600 font-medium">{styles.length} styles</span>
                </div>
                <div className="relative">
                  {isLoadingStyles ? (
                    <div className="flex space-x-3 overflow-x-auto scrollbar-hide pb-2">
                      {[1, 2, 3, 4, 5].map((item) => (
                        <div key={item} className="flex-shrink-0 w-36 h-36 bg-gray-200 rounded-lg animate-pulse flex flex-col items-center justify-center border border-gray-200">
                          <div className="w-8 h-8 bg-gray-300 rounded mb-2"></div>
                          <div className="w-20 h-3 bg-gray-300 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : styles.length > 0 ? (
                    <div className="flex space-x-3 overflow-x-auto scrollbar-hide pb-2">
                      {styles.map((product, index) => (
                        product.templates?.[0]?.design && getGlobalResources().length > 0 ? (
                          // 使用ProductDesignRenderer组件（点击跳转到自定义设计）
                          <button
                            key={product.code+index}
                            onClick={() => handleCustomizeClick(product)}
                            className="flex-shrink-0 w-36 h-36 bg-gray-200 flex flex-col items-center justify-center border border-gray-200"
                          >
                            <ProductDesignRenderer
                              design={product.templates[0].design}
                              previews={product.previews}
                              containerWidth={400}
                              containerHeight={400}
                              index={"other" + index}
                              className="w-full h-full"
                            />
                          </button>
                        ) : (
                          // 如果没有design属性，使用默认图片显示（点击跳转到自定义设计）
                          <button
                            key={product.code}
                            onClick={() => handleCustomizeClick(product)}
                            className="flex-shrink-0 w-36 h-36 bg-gray-200 flex items-center justify-center border border-gray-200"
                          >
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </button>
                        )
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="flex justify-center mb-2">
                        <RectangleStackIcon className="w-12 h-12 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm">No styles available</p>
                    </div>
                  )}
                  {/* Gradient overlay for smooth edge */}
                  <div className="absolute right-0 top-0 bottom-2 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Product Information */}
          <div ref={rightSidebarRef} className="col-span-4 h-screen overflow-y-auto pr-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="space-y-6 pb-8">

              {/* Pricing */}
              <div>
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-3xl font-bold text-gray-900">
                    ${selectedCombination?.price || loadedProduct.price}
                  </span>
                  <span className="text-lg text-gray-500 line-through">
                    ${(Number(selectedCombination?.price) || loadedProduct.price)}
                  </span>
                  {/* <span className="text-sm text-gray-500">Comp. value</span> */}
                </div>
                {/* <p className="text-sm text-green-600 font-medium">
                  Save 15% with code AUGUSTSAVE15
                </p> */}
              </div>

              {/* Product Title */}
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                    {(loadedProduct.name || '').length > 60
                  ? `${(loadedProduct.name || '').slice(0, 60)}...`
                  : (loadedProduct.name || '')}

                  {cacheKey && (
                    <span className="ml-2 text-sm px-2 py-1 rounded" style={{ color: '#2e3a47', backgroundColor: '#f0f2f5' }}>
                      Custom Design
                    </span>
                  )}
                </h1>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIconSolid
                          key={star}
                          className={`h-4 w-4 ${star <= (loadedProduct.rating || 0) ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      ({loadedProduct.reviews ? loadedProduct.reviews.toLocaleString() : '0'})
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">by Sweet Rain Paperie</p>
                </div>
              </div>
              {/* Product Description */}
              {loadedProduct.description && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {isDescriptionExpanded
                      ? loadedProduct.description
                      : ((loadedProduct.description || '').length > 140
                        ? (loadedProduct.description || '').slice(0, 140) + '…'
                        : (loadedProduct.description || ''))}
                  </p>
                  {(loadedProduct.description || '').length > 60 && (
                    <button
                      type="button"
                      className="mt-2 text-sm font-medium"
                      style={{ color: '#2e3a47' }}
                      onClick={() => setIsDescriptionExpanded(prev => !prev)}
                    >
                      {isDescriptionExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}

              {/* Pc Shipping Information */}
              {/* {loadedProduct.templates[0].id && loadedProduct.templates[0].id != "0" && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f2f5' }}>
                <p className="text-sm text-gray-700">
                  Order today and get it by {new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} with expedited shipping
                </p>
              </div>
              )} */}

              {cacheKey?.startsWith('AI') && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f2f5' }}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements</label>
                <textarea
                  value={specialRequirements}
                  onChange={(e) => setSpecialRequirements(e.target.value)}
                  placeholder="Please enter more customization requirements"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  rows={3}
                />
              </div>
              )}

              {/* Quantity and Add to Cart */}
              <div className="flex items-end space-x-3">
                <div className="w-32">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Qty:</label>
                    <div className="relative flex-1">
                      {showQuantitySelect ? (
                        <select
                          value={quantity}
                          onChange={(e) => {
                            setQuantity(Number(e.target.value));
                            setShowQuantitySelect(false);
                          }}
                          onBlur={() => setShowQuantitySelect(false)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:border-transparent"
                          style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                          onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#2e3a47')}
                          autoFocus
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 50, 100].map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={quantity}
                            onChange={(e) => setQuantity(Number(e.target.value) || 1)}
                            className="w-full border border-gray-300 rounded-lg px-3 pr-8 py-2 focus:ring-2 focus:border-transparent"
                            style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                            onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#2e3a47')}
                          />
                          <button
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer z-10"
                            onClick={() => setShowQuantitySelect(true)}
                          >
                            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={isAddedToCart}
                  className={`flex-1 py-2 px-6 rounded-lg font-semibold transition-colors ${isAddedToCart
                    ? 'bg-green-500 text-white cursor-not-allowed'
                    : 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                    }`}
                >
                  {isAddedToCart ? (
                    <div className="flex items-center justify-center">
                      <CheckIcon className="w-4 h-4 mr-1" />
                      Added
                    </div>
                  ) : (
                    'Add to Cart'
                  )}
                </button>
              </div>

              {/* Personalize Button */}
              <div className="space-y-2">

                {/* <Link
                  to={`/customize/${loadedProduct.id}`}
                  className="w-full text-white py-3 px-6 rounded-lg font-semibold transition-colors text-center block"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  Personalize this design
                </Link> */}
                 {cacheKey && !cacheKey.startsWith('AI') && (
                  <button
                    onClick={() => {

                      if (!isUserLoggedIn()) {
                        window.dispatchEvent(new Event('openLoginModal'));
                        return;
                      }
                      // 缓存当前的设计状态
                      const now = new Date();
                      const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1天后过期


                      // 使用函数更新 loadedProduct 中的图层内容
                      const updatedProduct = updateProductTemplateLayers();

                      const cacheData = {
                        key: cacheKey,
                        product: updatedProduct,
                        timestamp: now.toISOString(),
                        expiresAt: expiresAt.toISOString(),
                        version: '1.0'
                      };
                      localStorage.setItem(`design_${cacheKey}`, JSON.stringify(cacheData));

                      // 跳转到编辑页面
                      window.location.href = `/customize/${loadedProduct.id}?cacheKey=${cacheKey}`;
                    }}
                    className="w-full text-white py-2 px-6 rounded-lg font-medium transition-colors text-center block text-sm"
                    style={{ backgroundColor: '#68aeb0' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
                  >
                    Personalize this design
                  </button>
                )}
              </div>

              {/* Trending Indicator */}
              {/* <div className="flex items-center space-x-2 p-3 rounded-lg" style={{ backgroundColor: '#f0f2f5' }}>
                <SparklesIcon className="w-5 h-5" style={{ color: '#2e3a47' }} />
                <div>
                  <p className="text-xs" style={{ color: '#2e3a47' }}>{loadedProduct.reviews} people reviews this design</p>
                </div>
              </div> */}

              {/* Product layers */}
              {loadedProduct.templates[0].id && loadedProduct.templates[0].id != "0" && (
                <ProductLayersEditor 
                  textLayers={textLayers}
                  frameLayers={frameLayers}
                  onTextChange={handleTextChange}
                  onFrameAIChange={handleFrameAIChange}
                  onFrameImageChange={handleFrameImageChange}
                />
              )}



              {/* 2D Options: Color / Size */}

              {colorOptions.length > 0 && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {colorOptions.map((c) => (
                      <button
                        key={c.name}
                        onClick={() => setSelectedColorName(c.name)}
                        className={`flex items-center space-x-2 px-2 py-1 rounded-lg border ${selectedColorName === c.name ? '' : 'border-gray-300'}`}
                        style={selectedColorName === c.name ? { borderColor: '#2e3a47' } : {}}
                      >
                        {c.src ? (
                          <img src={c.src} alt={c.name} className="w-6 h-6 rounded object-cover" />
                        ) : (
                          <span className="w-6 h-6 rounded bg-gray-200 inline-block" />
                        )}
                        <span className="text-xs sm:text-sm text-gray-700">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {sizeOptions.length > 0 && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Size</label>
                  <div className="flex flex-wrap gap-2">
                    {sizeOptions.map((s) => (
                      <button
                        key={s.name}
                        onClick={() => setSelectedSizeName(s.name)}
                        className={`px-3 py-1 rounded-lg border text-xs sm:text-sm ${selectedSizeName === s.name ? '' : 'border-gray-300'}`}
                        style={selectedSizeName === s.name ? { borderColor: '#2e3a47' } : {}}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Scroll to Bottom Button - Fixed at bottom */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 pt-4 pb-2">
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    if (rightSidebarRef.current) {
                      rightSidebarRef.current.scrollTo({
                        top: rightSidebarRef.current.scrollHeight,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span>More options below</span>
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Recommended Products */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Guess you like</h3>
            <span className="text-xs sm:text-sm text-gray-600">{recommendedProducts.length} items</span>
          </div>

          {isLoadingRecommended ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-4">
              {[1, 2, 3].map((item) => (
                <div key={item} className="aspect-square bg-gray-100 rounded-lg animate-pulse flex items-center justify-center">
                  <div className="w-8 h-8 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          ) : recommendedProducts.length == 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-4">
              {recommendedProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  viewMode="grid"
                  hasResource={getGlobalResources().length > 0}
                />
              ))
              }
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="flex justify-center mb-2">
                <GiftIcon className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">No recommended products available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
